// MapMarker.jsx
import { useRef, memo, useEffect, useCallback, useState } from "react";
import { Marker, useMap } from "@vis.gl/react-maplibre";
import "./map-marker.css";

const SNAP_DISTANCE = 0.05;
const ANIM_DURATION = 900;
const easeInOut = (t) => t * t * (3 - 2 * t);

function zoomToScale(zoom) {
  if (zoom >= 14) return 1.15;
  if (zoom >= 12) return 1.0;
  if (zoom >= 10) return 0.85;
  if (zoom >= 8) return 0.65;
  if (zoom >= 6) return 0.45;
  return 0.3;
}

function formatShortAddress(address) {
  if (!address) return "Locating...";
  return address
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .slice(0, 2)
    .join(", ");
}

function useZoomScale() {
  const { map } = useMap();
  const [scale, setScale] = useState(() => zoomToScale(map?.getZoom() ?? 12));
  useEffect(() => {
    if (!map) return;
    const update = () => setScale(zoomToScale(map.getZoom()));
    map.on("zoomend", update);
    return () => map.off("zoomend", update);
  }, [map]);
  return scale;
}

function useAnimatedPosition(lat, lng) {
  const [pos, setPos] = useState({ lat, lng });
  const prevRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    if (lat == null || lng == null) return;
    const next = { lat, lng };
    const prev = prevRef.current;
    if (!prev) {
      prevRef.current = next;
      setPos(next);
      return;
    }
    const dist = Math.abs(next.lat - prev.lat) + Math.abs(next.lng - prev.lng);
    if (dist > SNAP_DISTANCE) {
      cancelAnimationFrame(animRef.current);
      prevRef.current = next;
      setPos(next);
      return;
    }
    const start = performance.now();
    cancelAnimationFrame(animRef.current);
    const tick = (now) => {
      const t = Math.min((now - start) / ANIM_DURATION, 1);
      const e = easeInOut(t);
      setPos({
        lat: prev.lat + (next.lat - prev.lat) * e,
        lng: prev.lng + (next.lng - prev.lng) * e,
      });
      if (t < 1) animRef.current = requestAnimationFrame(tick);
      else prevRef.current = next;
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
  }, [lat, lng]);

  return pos;
}

const MarkerLabel = ({ fleet, isSelected, isTracking, hovered }) => {
  const isPermanent = isSelected || isTracking;
  const showMain = isPermanent || hovered;
  const showExpand = isPermanent && hovered;

  if (!showMain) return null;

  const vehicleName =
    fleet.vehicleCode || `VEH-${fleet.vehicleId?.slice(-4).toUpperCase()}`;
  const speed = Math.round(fleet.speedKmh || 0);
  const address = formatShortAddress(fleet.address);

  return (
    <div
      className={`mkr-tooltip ${isPermanent ? "mkr-tooltip--permanent" : "mkr-tooltip--hover"}`}
    >
      <div className="tt-main">
        <span className="tt-rego">{vehicleName}</span>
        <span className="tt-sep" />
        <span className={`tt-speed ${speed > 0 ? "moving" : ""}`}>
          {speed}
          <small>KM/H</small>
        </span>
      </div>
      <div className="tt-expand">
        <div className="hud-address">
          <i className="bi bi-geo-alt-fill hud-pin-icon" />
          <span>{address}</span>
        </div>
        <div className="hud-coords">
          <span>{fleet.lat?.toFixed(5)}°</span>
          <span className="hud-coords-sep">,</span>
          <span>{fleet.lng?.toFixed(5)}°</span>
        </div>
      </div>
    </div>
  );
};

const PinElement = ({ status, isSelected, hasAlert, scale }) => {
  const isOnline = status === "online";
  const isIdle = status === "idle";
  const statusColor = hasAlert
    ? "var(--f-warning, #e11d48)"
    : isOnline
      ? "var(--f-success, #10b981)"
      : isIdle
        ? "var(--f-warning, #f59e0b)"
        : "var(--f-ignore, #64748b)";
  const W = isSelected ? 40 : 32;
  const H = Math.round(W * 1.18);
  const showSonar = isOnline || hasAlert;
  const sonarSize = isSelected ? W + 28 : W + 18;
  const sonarOff = -(sonarSize - W) / 2;

  return (
    <div
      className={[
        "mkr-root",
        isSelected ? "mkr-selected" : "",
        hasAlert ? "mkr-alert" : "",
        isOnline ? "mkr-online" : isIdle ? "mkr-idle" : "mkr-offline",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        "--sz": `${W}px`,
        "--h": `${H}px`,
        "--sc": statusColor,
        "--zoom-scale": scale,
      }}
    >
      {showSonar && (
        <div
          className="mkr-sonar"
          style={{
            width: sonarSize,
            height: sonarSize,
            top: sonarOff,
            left: sonarOff,
          }}
        >
          <div className="mkr-ring r1" />
          {isSelected && <div className="mkr-ring r2" />}
          {isSelected && <div className="mkr-ring r3" />}
        </div>
      )}
      <div className="gps-pin-shape">
        <div className="gps-pin-core">
          <div className="gps-pin-core-shine" />
        </div>
      </div>
    </div>
  );
};

const MapMarker = ({ fleet, isSelected, isTracking, onTrack, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const scale = useZoomScale();
  const animPos = useAnimatedPosition(fleet.lat, fleet.lng);

  const handleClick = useCallback(
    () => onClick?.(fleet.vehicleId),
    [onClick, fleet.vehicleId],
  );

  if (fleet.lat == null || fleet.lng == null) return null;

  return (
    <Marker
      longitude={animPos.lng}
      latitude={animPos.lat}
      anchor="bottom"
      style={{ zIndex: isSelected ? 999 : isTracking ? 998 : 1 }}
    >
      {/*
        mkr-wrapper uses onMouseEnter/Leave.
        The tooltip has pointer-events: auto when permanent so hovering
        it doesn't trigger onMouseLeave on the wrapper.
      */}
      <div
        className="mkr-wrapper"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={handleClick}
      >
        <MarkerLabel
          fleet={fleet}
          isSelected={isSelected}
          isTracking={isTracking}
          hovered={hovered}
        />
        <PinElement
          status={fleet.status || "offline"}
          isSelected={isSelected}
          hasAlert={fleet.hasAlert}
          scale={scale}
        />
      </div>
    </Marker>
  );
};

export default memo(MapMarker, (prev, next) => {
  const p = prev.fleet,
    n = next.fleet;
  if (!p || !n) return false;
  if (p.lat !== n.lat) return false;
  if (p.lng !== n.lng) return false;
  if (prev.isSelected !== next.isSelected) return false;
  if (prev.isTracking !== next.isTracking) return false;
  if (p.status !== n.status) return false;
  if (p.hasAlert !== n.hasAlert) return false;
  if (prev.isSelected || prev.isTracking) {
    if (p.speedKmh !== n.speedKmh) return false;
    if (p.address !== n.address) return false;
    if (p.timestamp !== n.timestamp) return false;
    if (p.employee?._id !== n.employee?._id) return false;
  }
  return true;
});
